import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, TerminalIcon, DatabaseIcon, WebhookIcon, CodeIcon, ShieldCheckIcon, ActivityIcon, LayersIcon, CreditCardIcon, CheckCircle2Icon } from 'lucide-react';
import { Terminal } from '../components/ui/Terminal';
export function LandingPage() {
  return <div className="bg-landing-bg text-landing-text min-h-screen" data-id="element-723">
      {/* Hero Section */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 px-4 relative overflow-hidden" data-id="element-724">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-landing-accent/10 via-landing-bg to-landing-bg -z-10" data-id="element-725"></div>

        <div className="container mx-auto max-w-5xl text-center" data-id="element-726">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-landing-surface border border-landing-border text-landing-accent text-sm font-medium mb-8" data-id="element-727">
            <span className="relative flex h-2 w-2" data-id="element-728">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-landing-accent opacity-75" data-id="element-729"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-landing-accent" data-id="element-730"></span>
            </span>
            Monetization Infrastructure for Developers
          </div>

          <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 leading-tight" data-id="element-731">
            Turn any app into a paid product{' '}
            <span className="text-landing-accent" data-id="element-732">instantly.</span>
          </h1>

          <p className="text-xl text-landing-muted max-w-2xl mx-auto mb-10 leading-relaxed" data-id="element-733">
            PayKit generates the full monetization infrastructure for your
            application. Stop reading Stripe docs and start building your
            product.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16" data-id="element-734">
            <Link to="/setup" className="w-full sm:w-auto bg-landing-accent text-white hover:bg-landing-accent-light px-8 py-4 rounded-lg text-lg font-semibold transition-colors flex items-center justify-center gap-2 shadow-lg shadow-landing-accent/20" data-id="element-735">
              Start Free Setup
              <ArrowRightIcon className="w-5 h-5" data-id="element-736" />
            </Link>
            <a href="#architecture" className="w-full sm:w-auto bg-landing-surface border border-landing-border text-landing-text hover:bg-landing-card px-8 py-4 rounded-lg text-lg font-semibold transition-colors flex items-center justify-center gap-2" data-id="element-737">
              View Architecture Preview
            </a>
          </div>

          {/* Terminal Preview */}
          <div className="max-w-3xl mx-auto text-left" data-id="element-738">
            <Terminal commands={[{
            input: 'npx paykit monetize',
            output: ['✔ detected Next.js', '✔ detected Supabase', '✔ configuring Stripe', '✔ generating billing schema', '✔ adding webhook handlers', '✔ adding entitlement checks', '', '✨ Payments live.'],
            delay: 800
          }]} className="shadow-2xl shadow-landing-accent/10 border-landing-border" data-id="element-739" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 border-y border-landing-border bg-landing-surface/50" data-id="element-740">
        <div className="container mx-auto px-4" data-id="element-741">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center" data-id="element-742">
            <div data-id="element-743">
              <div className="text-4xl md:text-5xl font-bold text-landing-accent mb-2" data-id="element-744">
                10x
              </div>
              <div className="text-sm font-medium text-landing-muted uppercase tracking-wider" data-id="element-745">
                Faster Setup
              </div>
            </div>
            <div data-id="element-746">
              <div className="text-4xl md:text-5xl font-bold text-landing-accent mb-2" data-id="element-747">
                0
              </div>
              <div className="text-sm font-medium text-landing-muted uppercase tracking-wider" data-id="element-748">
                Config Drift
              </div>
            </div>
            <div data-id="element-749">
              <div className="text-4xl md:text-5xl font-bold text-landing-accent mb-2" data-id="element-750">
                100%
              </div>
              <div className="text-sm font-medium text-landing-muted uppercase tracking-wider" data-id="element-751">
                Stripe Compliant
              </div>
            </div>
            <div data-id="element-752">
              <div className="text-4xl md:text-5xl font-bold text-landing-accent mb-2" data-id="element-753">
                &lt;5m
              </div>
              <div className="text-sm font-medium text-landing-muted uppercase tracking-wider" data-id="element-754">
                Time to Live
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Primary UX Flow */}
      <section className="py-24 px-4" data-id="element-755">
        <div className="container mx-auto max-w-5xl" data-id="element-756">
          <div className="text-center mb-16" data-id="element-757">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-758">
              The complete monetization flow
            </h2>
            <p className="text-lg text-landing-muted" data-id="element-759">
              From idea to fully provisioned infrastructure in minutes.
            </p>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-4 relative" data-id="element-760">
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 bg-landing-border -z-10 -translate-y-1/2" data-id="element-761"></div>

            {[{
            icon: TerminalIcon,
            label: 'Run Wizard'
          }, {
            icon: LayersIcon,
            label: 'Review Blueprint'
          }, {
            icon: CreditCardIcon,
            label: 'Connect Stripe'
          }, {
            icon: CodeIcon,
            label: 'Get Code Pack'
          }].map((step, i) => <div key={i} className="flex flex-col items-center gap-4 bg-landing-bg p-2" data-id="element-762">
                <div className="w-16 h-16 rounded-2xl bg-landing-card border border-landing-border flex items-center justify-center text-landing-accent shadow-lg" data-id="element-763">
                  <step.icon className="w-8 h-8" data-id="element-764" />
                </div>
                <span className="font-medium text-landing-text" data-id="element-765">
                  {step.label}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* Architecture Blueprint Screen */}
      <section id="architecture" className="py-24 px-4 bg-landing-surface" data-id="element-766">
        <div className="container mx-auto max-w-6xl" data-id="element-767">
          <div className="grid md:grid-cols-2 gap-16 items-center" data-id="element-768">
            <div data-id="element-769">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-landing-bg border border-landing-border text-landing-accent text-sm font-medium mb-6" data-id="element-770">
                <DatabaseIcon className="w-4 h-4" data-id="element-771" /> Architecture Blueprint
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-772">
                We design the architecture. <br data-id="element-773" />
                <span className="text-landing-muted" data-id="element-774">You own the code.</span>
              </h2>
              <p className="text-lg text-landing-muted mb-8 leading-relaxed" data-id="element-775">
                PayKit generates a complete, production-ready monetization
                architecture tailored to your stack. We configure Stripe,
                generate your database schema, and write your webhook handlers.
              </p>

              <ul className="space-y-4" data-id="element-776">
                {['Pricing models & subscription lifecycle', 'Webhook event routing & idempotency', 'Database schema for customers & subscriptions', 'Generated API endpoints for your framework'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-777">
                    <CheckCircle2Icon className="w-6 h-6 text-landing-accent shrink-0" data-id="element-778" />
                    <span className="text-landing-text" data-id="element-779">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="bg-landing-card border border-landing-border rounded-xl p-6 shadow-2xl" data-id="element-780">
              <div className="flex items-center gap-2 mb-6 pb-4 border-b border-landing-border" data-id="element-781">
                <div className="w-3 h-3 rounded-full bg-red-500/20 border border-red-500/50" data-id="element-782"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500/20 border border-yellow-500/50" data-id="element-783"></div>
                <div className="w-3 h-3 rounded-full bg-green-500/20 border border-green-500/50" data-id="element-784"></div>
                <span className="ml-2 text-xs font-mono text-landing-muted" data-id="element-785">
                  blueprint.yml
                </span>
              </div>

              <div className="space-y-6 font-mono text-sm" data-id="element-786">
                <div data-id="element-787">
                  <div className="text-landing-muted mb-2" data-id="element-788">
                    # Stripe Configuration
                  </div>
                  <div className="bg-landing-bg p-3 rounded border border-landing-border" data-id="element-789">
                    <div className="text-landing-accent" data-id="element-790">
                      Product:{' '}
                      <span className="text-landing-text" data-id="element-791">Pro Plan</span>
                    </div>
                    <div className="text-landing-accent" data-id="element-792">
                      Price:{' '}
                      <span className="text-landing-text" data-id="element-793">$29/month</span>
                    </div>
                  </div>
                </div>

                <div data-id="element-794">
                  <div className="text-landing-muted mb-2" data-id="element-795">
                    # Required Webhooks
                  </div>
                  <div className="bg-landing-bg p-3 rounded border border-landing-border flex flex-wrap gap-2" data-id="element-796">
                    <span className="px-2 py-1 bg-landing-surface rounded text-landing-text" data-id="element-797">
                      checkout.session.completed
                    </span>
                    <span className="px-2 py-1 bg-landing-surface rounded text-landing-text" data-id="element-798">
                      invoice.paid
                    </span>
                    <span className="px-2 py-1 bg-landing-surface rounded text-landing-text" data-id="element-799">
                      customer.subscription.deleted
                    </span>
                  </div>
                </div>

                <div data-id="element-800">
                  <div className="text-landing-muted mb-2" data-id="element-801">
                    # Database Schema
                  </div>
                  <div className="bg-landing-bg p-3 rounded border border-landing-border flex gap-4" data-id="element-802">
                    <span className="flex items-center gap-1 text-landing-text" data-id="element-803">
                      <DatabaseIcon className="w-3 h-3 text-landing-accent" data-id="element-804" />{' '}
                      subscriptions
                    </span>
                    <span className="flex items-center gap-1 text-landing-text" data-id="element-805">
                      <DatabaseIcon className="w-3 h-3 text-landing-accent" data-id="element-806" />{' '}
                      payments
                    </span>
                    <span className="flex items-center gap-1 text-landing-text" data-id="element-807">
                      <DatabaseIcon className="w-3 h-3 text-landing-accent" data-id="element-808" />{' '}
                      customers
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Provisioning & Monitoring */}
      <section className="py-24 px-4" data-id="element-809">
        <div className="container mx-auto max-w-6xl" data-id="element-810">
          <div className="grid md:grid-cols-2 gap-8" data-id="element-811">
            {/* Provisioning Card */}
            <div className="bg-landing-card border border-landing-border rounded-2xl p-8 hover:border-landing-accent/50 transition-colors" data-id="element-812">
              <div className="w-12 h-12 rounded-lg bg-landing-accent/10 text-landing-accent flex items-center justify-center mb-6" data-id="element-813">
                <ActivityIcon className="w-6 h-6" data-id="element-814" />
              </div>
              <h3 className="text-2xl font-bold mb-4" data-id="element-815">
                Automated Provisioning
              </h3>
              <p className="text-landing-muted mb-8" data-id="element-816">
                Review the provisioning plan before we touch your Stripe
                account. Exactly like Terraform for your payments.
              </p>

              <div className="bg-landing-bg border border-landing-border rounded-lg p-4 font-mono text-sm space-y-3" data-id="element-817">
                <div className="text-landing-muted" data-id="element-818">
                  PayKit will create in Stripe:
                </div>
                <div className="flex items-center gap-2 text-landing-text" data-id="element-819">
                  <span className="text-landing-accent" data-id="element-820">+</span> Product: Pro
                  Plan
                </div>
                <div className="flex items-center gap-2 text-landing-text" data-id="element-821">
                  <span className="text-landing-accent" data-id="element-822">+</span> Price:
                  $29/month
                </div>
                <div className="flex items-center gap-2 text-landing-text" data-id="element-823">
                  <span className="text-landing-accent" data-id="element-824">+</span> Webhook
                  endpoint
                </div>
              </div>
            </div>

            {/* Monitoring Card */}
            <div className="bg-landing-card border border-landing-border rounded-2xl p-8 hover:border-landing-accent/50 transition-colors" data-id="element-825">
              <div className="w-12 h-12 rounded-lg bg-landing-accent/10 text-landing-accent flex items-center justify-center mb-6" data-id="element-826">
                <ShieldCheckIcon className="w-6 h-6" data-id="element-827" />
              </div>
              <h3 className="text-2xl font-bold mb-4" data-id="element-828">Active Monitoring</h3>
              <p className="text-landing-muted mb-8" data-id="element-829">
                We monitor your webhooks and Stripe configuration for drift,
                ensuring your payments never silently fail.
              </p>

              <div className="bg-landing-bg border border-red-500/30 rounded-lg p-4" data-id="element-830">
                <div className="flex items-start gap-3" data-id="element-831">
                  <div className="w-2 h-2 mt-1.5 rounded-full bg-red-500 animate-pulse" data-id="element-832"></div>
                  <div data-id="element-833">
                    <div className="font-semibold text-red-400 mb-1" data-id="element-834">
                      Webhook failure detected
                    </div>
                    <div className="text-sm text-landing-muted mb-3 font-mono" data-id="element-835">
                      invoice.payment_failed event not processed
                    </div>
                    <button className="px-3 py-1.5 bg-landing-surface border border-landing-border rounded text-xs font-medium hover:bg-landing-card transition-colors" data-id="element-836">
                      Retry Event
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-4 relative overflow-hidden" data-id="element-837">
        <div className="absolute inset-0 bg-landing-accent/5" data-id="element-838"></div>
        <div className="container mx-auto max-w-4xl text-center relative z-10" data-id="element-839">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-840">
            Ready to monetize?
          </h2>
          <p className="text-xl text-landing-muted mb-10 max-w-2xl mx-auto" data-id="element-841">
            Join developers building the next generation of SaaS products with
            PayKit's monetization infrastructure.
          </p>
          <Link to="/setup" className="inline-flex items-center gap-2 bg-landing-accent text-white hover:bg-landing-accent-light px-8 py-4 rounded-lg text-lg font-bold transition-colors shadow-xl shadow-landing-accent/20" data-id="element-842">
            Start Free Setup
            <ArrowRightIcon className="w-5 h-5" data-id="element-843" />
          </Link>
        </div>
      </section>
    </div>;
}